/**
 * Alta y administración de usuarios.
 *
 * El problema que resuelve este módulo: `createUserWithEmailAndPassword`
 * **inicia sesión** con el usuario recién creado. Llamado desde la sesión del
 * administrador, lo expulsaría de su propia cuenta a mitad del trámite.
 *
 * La salida es levantar una segunda instancia de Firebase, sólo para esto,
 * con persistencia en memoria: crea la cuenta, devuelve el identificador y se
 * cierra. La sesión del administrador ni se entera. El perfil (con el rol) se
 * escribe desde la instancia principal, que es la que tiene los permisos.
 *
 * Lo que no se puede hacer desde aquí: borrar una cuenta de Authentication.
 * Eso exige credenciales de servidor. Por eso las cuentas se **desactivan**
 * (`activo: false`), y las reglas de Firestore le niegan todo a quien esté
 * desactivado.
 */

import { initializeApp, deleteApp, type FirebaseApp } from 'firebase/app';
import {
  createUserWithEmailAndPassword,
  initializeAuth,
  inMemoryPersistence,
  sendPasswordResetEmail,
  signOut,
} from 'firebase/auth';
import { collection, doc, getDocs, query, setDoc, updateDoc, where } from 'firebase/firestore';
import { auth, configFirebase, db, COL, SIN_CONFIGURAR } from './firebase';
import type { Perfil, Rol } from '@/hooks/useAuth';

export interface DatosAlta {
  nombre: string;
  email: string;
  clave: string;
  rol: Rol;
  plantaId: string | null;
}

/** Traduce los códigos de Firebase a algo que se entienda. */
function mensaje(e: unknown): string {
  const codigo = (e as { code?: string })?.code ?? '';
  switch (codigo) {
    case 'auth/email-already-in-use':
      return 'Ya existe una cuenta con ese correo.';
    case 'auth/invalid-email':
      return 'El correo no tiene un formato válido.';
    case 'auth/weak-password':
      return 'La contraseña es muy corta: mínimo 6 caracteres.';
    case 'auth/network-request-failed':
      return 'Sin conexión. Revisa la red e inténtalo de nuevo.';
    case 'permission-denied':
      return 'Tu usuario no tiene permiso para esto. ¿Publicaste la última versión de las reglas?';
    default:
      return e instanceof Error ? e.message : 'No se pudo completar la operación.';
  }
}

/** Lo que viene de Firestore, ya normalizado. */
function aPerfil(uid: string, x: Partial<Perfil>): Perfil {
  return {
    uid,
    email: x.email ?? '',
    nombre: x.nombre ?? '',
    rol: (x.rol as Rol) ?? 'LECTOR',
    plantaId: x.plantaId ?? null,
    activo: x.activo ?? true,
    oculto: x.oculto ?? false,
  };
}

const porNombre = (a: Perfil, b: Perfil) =>
  (a.nombre || a.email).localeCompare(b.nombre || b.email, 'es');

/**
 * Quita los perfiles ocultos si quien mira no lo está.
 *
 * Las reglas ya lo impiden desde la base, pero el filtro vale igual: deja la
 * pantalla consistente mientras las reglas nuevas no estén publicadas, y
 * evita que un perfil oculto se cuele por una lectura que las reglas sí
 * permiten, como la del propio administrador oculto mirando su lista.
 */
function visiblesPara(perfiles: Perfil[], yo: string | null): Perfil[] {
  const miro = perfiles.find((p) => p.uid === yo);
  if (miro?.oculto) return perfiles;
  return perfiles.filter((p) => !p.oculto || p.uid === yo);
}

/**
 * Todos los perfiles. Sólo un administrador puede leerlos.
 *
 * Quien está oculto los lee todos de una. A los demás las reglas les niegan
 * el perfil oculto, y en Firestore un solo documento denegado hace fallar la
 * consulta entera: por eso, si la lectura abierta falla, se reintenta pidiendo
 * únicamente los visibles.
 *
 * Ese segundo intento sólo devuelve documentos que tengan el campo escrito
 * —`where` no encuentra los que no lo traen—, así que quien puede leerlos
 * todos aprovecha de completárselo a los que les falta.
 */
export async function listarUsuarios(): Promise<Perfil[]> {
  if (SIN_CONFIGURAR || !db) return [];
  const usuarios = collection(db, COL.usuarios);
  try {
    const snap = await getDocs(usuarios);
    const perfiles = snap.docs.map((d) => aPerfil(d.id, d.data() as Partial<Perfil>));

    const sinCampo = snap.docs.filter((d) => !('oculto' in d.data()));
    if (sinCampo.length) {
      await Promise.all(sinCampo.map((d) => updateDoc(d.ref, { oculto: false }).catch(() => {})));
    }
    return visiblesPara(perfiles, auth?.currentUser?.uid ?? null).sort(porNombre);
  } catch (e) {
    if ((e as { code?: string })?.code !== 'permission-denied') throw new Error(mensaje(e));
    try {
      const snap = await getDocs(query(usuarios, where('oculto', '==', false)));
      return snap.docs.map((d) => aPerfil(d.id, d.data() as Partial<Perfil>)).sort(porNombre);
    } catch (e2) {
      throw new Error(mensaje(e2));
    }
  }
}

/**
 * Crea la cuenta y su perfil. Devuelve el perfil ya escrito.
 * La sesión de quien la ejecuta queda intacta.
 */
export async function crearUsuario(d: DatosAlta): Promise<Perfil> {
  if (SIN_CONFIGURAR || !db) throw new Error('Firebase no está configurado.');

  const email = d.email.trim().toLowerCase();
  const nombre = d.nombre.trim();
  if (!nombre) throw new Error('Falta el nombre.');
  if (d.clave.length < 6) throw new Error('La contraseña es muy corta: mínimo 6 caracteres.');

  let secundaria: FirebaseApp | null = null;
  try {
    secundaria = initializeApp(configFirebase, `alta-${Date.now()}`);
    // En memoria: la sesión del nuevo usuario no toca el almacenamiento del
    // navegador ni pisa la del administrador.
    const authAlta = initializeAuth(secundaria, { persistence: inMemoryPersistence });

    const cred = await createUserWithEmailAndPassword(authAlta, email, d.clave);
    const uid = cred.user.uid;
    await signOut(authAlta);

    const perfil: Perfil = {
      uid,
      email,
      nombre,
      rol: d.rol,
      plantaId: d.plantaId,
      activo: true,
      // Nadie nace oculto: el campo se escribe siempre para que la consulta
      // de los visibles lo encuentre.
      oculto: false,
    };
    // Se escribe desde la instancia principal: es la que va autenticada como
    // administrador, y es lo que las reglas exigen.
    await setDoc(doc(db, COL.usuarios, uid), {
      ...perfil,
      creadoEn: new Date().toISOString(),
      creadoPor: auth?.currentUser?.uid ?? null,
    });
    return perfil;
  } catch (e) {
    throw new Error(mensaje(e));
  } finally {
    if (secundaria) await deleteApp(secundaria).catch(() => {});
  }
}

/** Cambia el rol de un usuario. */
export async function cambiarRol(uid: string, rol: Rol, plantaId: string | null): Promise<void> {
  if (!db) throw new Error('Firebase no está configurado.');
  try {
    await updateDoc(doc(db, COL.usuarios, uid), {
      rol,
      plantaId,
      actualizadoEn: new Date().toISOString(),
      actualizadoPor: auth?.currentUser?.uid ?? null,
    });
  } catch (e) {
    throw new Error(mensaje(e));
  }
}

/** Activa o desactiva el acceso. Desactivado equivale a sin permisos. */
export async function cambiarActivo(uid: string, activo: boolean): Promise<void> {
  if (!db) throw new Error('Firebase no está configurado.');
  try {
    await updateDoc(doc(db, COL.usuarios, uid), {
      activo,
      actualizadoEn: new Date().toISOString(),
      actualizadoPor: auth?.currentUser?.uid ?? null,
    });
  } catch (e) {
    throw new Error(mensaje(e));
  }
}

/**
 * Oculta o muestra un perfil frente a los demás administradores.
 *
 * Sólo se puede hacer sobre el propio perfil: que un administrador esconda a
 * otro sería una forma de dejarlo sin vigilancia.
 */
export async function cambiarOculto(uid: string, oculto: boolean): Promise<void> {
  if (!db) throw new Error('Firebase no está configurado.');
  if (auth?.currentUser?.uid !== uid) {
    throw new Error('Sólo puedes ocultar tu propio perfil.');
  }
  try {
    await updateDoc(doc(db, COL.usuarios, uid), {
      oculto,
      actualizadoEn: new Date().toISOString(),
      actualizadoPor: uid,
    });
  } catch (e) {
    throw new Error(mensaje(e));
  }
}

/** Manda el correo para que el usuario elija su propia contraseña. */
export async function enviarRestablecer(email: string): Promise<void> {
  if (!auth) throw new Error('Firebase no está configurado.');
  try {
    await sendPasswordResetEmail(auth, email.trim());
  } catch (e) {
    throw new Error(mensaje(e));
  }
}

/** Contraseña provisoria legible, para dictarla por teléfono. */
export function claveSugerida(): string {
  const silabas = ['ma', 'ru', 'te', 'lo', 'si', 'pa', 'ne', 'do', 'va', 'ki'];
  const al = (n: number) => Math.floor(Math.random() * n);
  const s = () => silabas[al(silabas.length)] ?? 'xa';
  return s() + s() + s() + '-' + String(100 + al(900));
}
